// Moderation page (disguised as cache status)

import { useEffect, useState, useCallback } from 'react'
import { getVideoUrl } from '@/lib/supabase'
import type { Video } from '@/types'

const API_URL = import.meta.env.VITE_API_URL

export default function AdminPage() {
  const [adminToken, setAdminToken] = useState<string | null>(null)
  const [password, setPassword] = useState('')
  const [videos, setVideos] = useState<Video[]>([])
  const [loading, setLoading] = useState(true)
  const [loginLoading, setLoginLoading] = useState(false)
  const [loginError, setLoginError] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [selectedVideo, setSelectedVideo] = useState<Video | null>(null)
  const [rejectReason, setRejectReason] = useState('')
  const [showRejectModal, setShowRejectModal] = useState(false)

  // Check for existing token on mount
  useEffect(() => {
    const stored = sessionStorage.getItem('admin_token')
    if (stored) {
      try {
        const decoded = atob(stored)
        const [, expiryStr] = decoded.split(':')
        const expiry = parseInt(expiryStr, 10)
        if (Date.now() < expiry) {
          setAdminToken(stored)
        } else {
          sessionStorage.removeItem('admin_token')
        }
      } catch {
        sessionStorage.removeItem('admin_token')
      }
    }
    setLoading(false)
  }, [])

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoginLoading(true)
    setLoginError(null)

    try {
      const res = await fetch(`${API_URL}/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      })

      const data = await res.json()

      if (!res.ok) {
        setLoginError(data.error || 'Login failed')
        return
      }

      sessionStorage.setItem('admin_token', data.adminToken)
      setAdminToken(data.adminToken)
      setPassword('')
    } catch (err) {
      setLoginError('Network error')
    } finally {
      setLoginLoading(false)
    }
  }

  const fetchVideos = useCallback(async () => {
    if (!adminToken) return

    try {
      setLoading(true)
      const res = await fetch(`${API_URL}/admin/pending`, {
        headers: { 'Authorization': `Bearer ${adminToken}` },
      })

      if (res.status === 401) {
        sessionStorage.removeItem('admin_token')
        setAdminToken(null)
        return
      }

      const data = await res.json()
      setVideos(data.videos || [])
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load')
    } finally {
      setLoading(false)
    }
  }, [adminToken])

  useEffect(() => {
    if (adminToken) {
      fetchVideos()
    }
  }, [adminToken, fetchVideos])

  const handleApprove = async (id: string, e?: React.MouseEvent) => {
    e?.stopPropagation()
    if (!adminToken) return

    try {
      setActionLoading(id)
      const res = await fetch(`${API_URL}/admin/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`,
        },
        body: JSON.stringify({ videoId: id }),
      })

      if (!res.ok) {
        const data = await res.json()
        throw new Error(data.error || 'Failed')
      }

      setVideos(videos.filter(v => v.id !== id))
      if (selectedVideo?.id === id) setSelectedVideo(null)
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed')
    } finally {
      setActionLoading(null)
    }
  }

  const handleReject = async (id: string) => {
    if (!adminToken) return

    try {
      setActionLoading(id)
      const res = await fetch(`${API_URL}/admin/reject`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`,
        },
        body: JSON.stringify({ videoId: id, reason: rejectReason || undefined }),
      })

      if (!res.ok) {
        const data = await res.json()
        throw new Error(data.error || 'Failed')
      }

      setVideos(videos.filter(v => v.id !== id))
      setSelectedVideo(null)
      setShowRejectModal(false)
      setRejectReason('')
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed')
    } finally {
      setActionLoading(null)
    }
  }

  const openRejectModal = (video: Video, e?: React.MouseEvent) => {
    e?.stopPropagation()
    setSelectedVideo(video)
    setShowRejectModal(true)
  }

  const handleLogout = () => {
    sessionStorage.removeItem('admin_token')
    setAdminToken(null)
  }

  // Keyboard shortcuts
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (!selectedVideo || showRejectModal) return
    if (e.key === 'a' || e.key === 'A') {
      handleApprove(selectedVideo.id)
    } else if (e.key === 'r' || e.key === 'R') {
      openRejectModal(selectedVideo)
    } else if (e.key === 'Escape') {
      setSelectedVideo(null)
    }
  }, [selectedVideo, showRejectModal, videos])

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown])

  // Login screen
  if (!adminToken) {
    return (
      <div className="flex-1 flex items-center justify-center p-6">
        <form onSubmit={handleLogin} className="bg-gray-800 p-6 rounded-lg w-full max-w-sm">
          <h1 className="text-xl font-bold text-white mb-2">Cache Debug</h1>
          <p className="text-gray-500 text-sm mb-4">Enter service key to view cache status</p>
          {loginError && (
            <p className="text-red-400 text-sm mb-4">{loginError}</p>
          )}
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Service key"
            className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white mb-4 focus:outline-none focus:border-blue-500"
            disabled={loginLoading}
          />
          <button
            type="submit"
            disabled={loginLoading}
            className="w-full px-4 py-2 bg-gray-600 hover:bg-gray-500 disabled:bg-gray-700 text-white font-medium rounded-lg transition-colors"
          >
            {loginLoading ? 'Authenticating...' : 'Authenticate'}
          </button>
        </form>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-gray-600 border-t-white rounded-full animate-spin" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="text-center">
          <p className="text-red-400 mb-4">{error}</p>
          <button
            onClick={fetchVideos}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex-1 p-4">
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <div>
          <h1 className="text-xl font-bold text-white">Review Queue</h1>
          <p className="text-gray-500 text-xs">{videos.length} pending • Click to preview • A=approve R=reject</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={fetchVideos}
            className="px-3 py-1 text-xs bg-gray-700 hover:bg-gray-600 rounded transition-colors"
          >
            Refresh
          </button>
          <button
            onClick={handleLogout}
            className="px-3 py-1 text-xs text-gray-400 hover:text-white transition-colors"
          >
            Logout
          </button>
        </div>
      </div>

      {videos.length === 0 ? (
        <div className="text-center py-12 bg-gray-800 rounded-lg">
          <div className="w-12 h-12 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-3">
            <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <p className="text-gray-400 text-sm">Queue empty</p>
        </div>
      ) : (
        /* Compact Grid */
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2">
          {videos.map((video) => (
            <div
              key={video.id}
              onClick={() => setSelectedVideo(video)}
              className={`relative bg-gray-800 rounded-lg overflow-hidden cursor-pointer group transition-all ${
                selectedVideo?.id === video.id ? 'ring-2 ring-blue-500' : 'hover:ring-1 hover:ring-gray-600'
              }`}
            >
              {/* Thumbnail Video */}
              <div className="aspect-video bg-gray-900">
                <video
                  src={getVideoUrl(video.r2_key) || ''}
                  className="w-full h-full object-cover"
                  preload="metadata"
                  muted
                  onMouseEnter={(e) => e.currentTarget.play().catch(() => {})}
                  onMouseLeave={(e) => { e.currentTarget.pause(); e.currentTarget.currentTime = 0 }}
                />
              </div>

              {/* Auto-recovered badge */}
              {video.auto_recovered && (
                <div className="absolute top-1 left-1 px-1.5 py-0.5 bg-orange-500 text-white text-[10px] font-bold rounded" title="Auto-recovered: User did not press stop">
                  RESCUED
                </div>
              )}

              {/* Quick Info Overlay */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-1.5">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-300 truncate">
                    {((video.file_size_bytes || 0) / 1024 / 1024).toFixed(1)}MB
                  </span>
                  {video.gps_lat && (
                    <span className="text-blue-400" title="Has location">📍</span>
                  )}
                </div>
              </div>

              {/* Quick Action Buttons (visible on hover) */}
              <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={(e) => handleApprove(video.id, e)}
                  disabled={actionLoading === video.id}
                  className="w-7 h-7 bg-green-600 hover:bg-green-500 disabled:bg-green-800 rounded flex items-center justify-center transition-colors"
                  title="Approve (A)"
                >
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </button>
                <button
                  onClick={(e) => openRejectModal(video, e)}
                  disabled={actionLoading === video.id}
                  className="w-7 h-7 bg-red-600 hover:bg-red-500 disabled:bg-red-800 rounded flex items-center justify-center transition-colors"
                  title="Reject (R)"
                >
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Loading Overlay */}
              {actionLoading === video.id && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Video Preview Modal */}
      {selectedVideo && !showRejectModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setSelectedVideo(null)}>
          <div className="bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            {/* Video Player */}
            <div className="aspect-video bg-gray-900">
              <video
                src={getVideoUrl(selectedVideo.r2_key) || ''}
                controls
                autoPlay
                className="w-full h-full"
              />
            </div>

            {/* Info & Actions */}
            <div className="p-4">
              <div className="grid grid-cols-4 gap-3 text-xs mb-4">
                <div>
                  <span className="text-gray-500 block">ID</span>
                  <span className="font-mono text-gray-300">{selectedVideo.id.slice(0, 8)}</span>
                </div>
                <div>
                  <span className="text-gray-500 block">Size</span>
                  <span className="text-gray-300">{((selectedVideo.file_size_bytes || 0) / 1024 / 1024).toFixed(2)} MB</span>
                </div>
                <div>
                  <span className="text-gray-500 block">Uploaded</span>
                  <span className="text-gray-300">{new Date(selectedVideo.created_at).toLocaleString()}</span>
                </div>
                <div>
                  <span className="text-gray-500 block">Location</span>
                  {selectedVideo.gps_lat && selectedVideo.gps_lng ? (
                    <a
                      href={`https://maps.google.com/?q=${selectedVideo.gps_lat},${selectedVideo.gps_lng}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-400 hover:text-blue-300"
                    >
                      View Map
                    </a>
                  ) : (
                    <span className="text-gray-500">None</span>
                  )}
                </div>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => handleApprove(selectedVideo.id)}
                  disabled={actionLoading === selectedVideo.id}
                  className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-500 disabled:bg-green-800 text-white font-medium rounded-lg transition-colors"
                >
                  {actionLoading === selectedVideo.id ? '...' : 'Approve (A)'}
                </button>
                <button
                  onClick={() => openRejectModal(selectedVideo)}
                  disabled={actionLoading === selectedVideo.id}
                  className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-500 disabled:bg-red-800 text-white font-medium rounded-lg transition-colors"
                >
                  Reject (R)
                </button>
                <button
                  onClick={() => setSelectedVideo(null)}
                  className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-lg transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && selectedVideo && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => { setShowRejectModal(false); setRejectReason('') }}>
          <div className="bg-gray-800 rounded-lg w-full max-w-md p-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-white mb-3">Reject Video</h3>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Reason (optional)"
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm resize-none focus:outline-none focus:border-red-500 mb-3"
              rows={2}
              autoFocus
            />
            <div className="flex gap-2">
              <button
                onClick={() => handleReject(selectedVideo.id)}
                disabled={actionLoading === selectedVideo.id}
                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-500 disabled:bg-red-800 text-white font-medium rounded-lg transition-colors"
              >
                {actionLoading === selectedVideo.id ? 'Rejecting...' : 'Confirm Reject'}
              </button>
              <button
                onClick={() => { setShowRejectModal(false); setRejectReason('') }}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
